import PageEditSessionRecord from "models/view-models/page-edit-session-record";
import { useCallback } from "react";
import { PageEditSessionService } from "utilities/services/notebooks/page-edit-session-service";

const POLLING_INTERVAL = 5000; // 5 seconds

const usePageEditSessions = (isPollingEnabled: boolean, pageId?: number) => {
    const { list } = PageEditSessionService.useListActiveSessions();
    const { create: createSession } = PageEditSessionService.useCreate();
    const { update } = PageEditSessionService.useUpdate();
    const { delete: endSession } = PageEditSessionService.useDelete();

    // Get all sessions if in edit mode. Refetch at polling interval
    const {
        data: activeSession,
        remove: removeActiveSessionFromState,
        refetch: refreshSessionList,
    } = list(
        { pageId: pageId! },
        {
            enabled: !!pageId && isPollingEnabled,
            refetchInterval: isPollingEnabled ? POLLING_INTERVAL : false,
        }
    );

    const startEditSession = useCallback(
        async (blockId?: number) => {
            if (!pageId) {
                return false;
            }
            try {
                const newSession = new PageEditSessionRecord({
                    pageId: pageId,
                    blockId: blockId,
                });
                await createSession(newSession, {
                    pageId: pageId!,
                    blockId: blockId ?? undefined,
                });

                return true;
            } catch (error) {
                return false;
            }
        },
        [createSession, pageId]
    );

    const updateEditSession = useCallback(
        async (sessionId: number, blockId?: number): Promise<boolean> => {
            try {
                await update({ blockId }, { id: sessionId });
                return true;
            } catch (error) {
                return false;
            }
        },
        [update]
    );

    const endEditSession = async (sessionId: number) => {
        try {
            await endSession(sessionId, {
                pageId,
            });
            removeActiveSessionFromState();
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    return {
        activeEditSessions: activeSession?.resultObjects ?? [],
        startEditSession,
        updateEditSession,
        endEditSession,
        refreshSessionList,
    };
};
export default usePageEditSessions;
